import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import HeadingSmall from "@/components/heading-small"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { resetOnboardingSettingsManagePath, settingsManagePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Settings",
    href: settingsManagePath(),
  },
]

// IMP-228: demo-only self-service reset. IMP-529 removed the lighter
// "Reset agents" — a demo account always carries its three live jobs, and
// that reset produced exactly the empty-catalog state the always-live rule
// makes unreachable. Reset-to-onboarding keeps the jobs: finishing (or
// skipping) the onboarding it triggers lands back on the live workspace.

export default function ManageSettings() {
  const [open, setOpen] = useState(false)

  function confirmReset() {
    router.post(resetOnboardingSettingsManagePath(), undefined, {
      preserveScroll: true,
      onFinish: () => setOpen(false),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Settings — Manage" />
      <SettingsLayout>
        <div className="space-y-12">
          <div className="space-y-4">
            <HeadingSmall
              title="Reset to onboarding"
              description="Send this account back to the beginning. Useful before a fresh demo walkthrough — clears the business profile and drops you on the onboarding flow. Your jobs and their activity stay, so finishing lands back on the live workspace."
            />
            <Button
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive/10"
              onClick={() => setOpen(true)}
            >
              Reset to onboarding
            </Button>
          </div>
        </div>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset to onboarding?</AlertDialogTitle>
              <AlertDialogDescription>
                Clears your business profile and returns the account to the
                start of onboarding. Jobs and their activity are kept. This can
                be re-run any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmReset}
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                Yes, reset to onboarding
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SettingsLayout>
    </AppLayout>
  )
}
